import { isPlatformBrowser } from '@angular/common'
import type { EnvironmentProviders, Provider } from '@angular/core'
import {
  EnvironmentInjector,
  PLATFORM_ID,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  runInInjectionContext,
} from '@angular/core'

import type { ProvideKeycloakOptions } from 'keycloak-angular'
import { KEYCLOAK_EVENT_SIGNAL, createKeycloakSignal } from 'keycloak-angular'
import Keycloak from 'keycloak-js'

const provideKeycloakInAppInitializer = (
  options: ProvideKeycloakOptions
): EnvironmentProviders | Provider[] => {
  const { initOptions, features = [] } = options

  if (!initOptions) {
    return []
  }

  return provideAppInitializer(async () => {
    const platform = inject(PLATFORM_ID)

    // only init keycloak in the browser
    if (isPlatformBrowser(platform)) {
      const injector = inject(EnvironmentInjector)
      runInInjectionContext(injector, () => {
        features.forEach(feature => {
          feature.configure()
        })
      })
    }
  })
}

export function provideKeycloakSSR(
  options: ProvideKeycloakOptions
): EnvironmentProviders {
  const keycloak = new Keycloak(options.config)

  const providers = options.providers ?? []
  const keycloakSignal = createKeycloakSignal(keycloak)

  return makeEnvironmentProviders([
    {
      provide: KEYCLOAK_EVENT_SIGNAL,
      useValue: keycloakSignal,
    },
    {
      provide: Keycloak,
      useValue: keycloak,
    },
    ...providers,
    provideKeycloakInAppInitializer(options),
  ])
}
